/**
 * Landscaping subservice flow: category (Cosmos `short_name` subservices),
 * plus a customer-only `not-sure` path for UX — not in cosmos-seeds.
 * Then specific task, then conditional follow-ups. Kept to display saved requests.
 */
import GrassIcon from '@mui/icons-material/Grass';
import AgricultureIcon from '@mui/icons-material/Agriculture';
import NatureIcon from '@mui/icons-material/Nature';
import CleaningServicesIcon from '@mui/icons-material/CleaningServices';
import ParkIcon from '@mui/icons-material/Park';
import WaterIcon from '@mui/icons-material/Water';
import DeckIcon from '@mui/icons-material/Deck';
import ContentCutIcon from '@mui/icons-material/ContentCut';
import SpaIcon from '@mui/icons-material/Spa';
import LayersIcon from '@mui/icons-material/Layers';
import YardIcon from '@mui/icons-material/Yard';
import LocalFloristIcon from '@mui/icons-material/LocalFlorist';
import BugReportIcon from '@mui/icons-material/BugReport';
import ForestIcon from '@mui/icons-material/Forest';
import ParkOutlinedIcon from '@mui/icons-material/ParkOutlined';
import ContentCutOutlinedIcon from '@mui/icons-material/ContentCutOutlined';
import ParkRoundedIcon from '@mui/icons-material/ParkRounded';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import WbSunnyIcon from '@mui/icons-material/WbSunny';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import FoundationIcon from '@mui/icons-material/Foundation';
import LandscapeIcon from '@mui/icons-material/Landscape';
import BorderOuterIcon from '@mui/icons-material/BorderOuter';
import TerrainIcon from '@mui/icons-material/Terrain';
import PlumbingIcon from '@mui/icons-material/Plumbing';
import BuildIcon from '@mui/icons-material/Build';
import ApartmentIcon from '@mui/icons-material/Apartment';
import OutdoorGrillIcon from '@mui/icons-material/OutdoorGrill';
import LightModeIcon from '@mui/icons-material/LightMode';
import FireplaceIcon from '@mui/icons-material/Fireplace';
import HolidayVillageIcon from '@mui/icons-material/HolidayVillage';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import HomeIcon from '@mui/icons-material/Home';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import TodayIcon from '@mui/icons-material/Today';
import DateRangeIcon from '@mui/icons-material/DateRange';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import ScheduleIcon from '@mui/icons-material/Schedule';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import WeekendIcon from '@mui/icons-material/Weekend';

// ─── Step 1: Category ───────────────────────────────────────────────────────
const LANDSCAPING_PROBLEMS = [
  { id: 'lawn-maintenance', label: 'Lawn Maintenance', icon: GrassIcon },
  { id: 'lawn-health-and-treatment', label: 'Lawn Health & Treatment', icon: AgricultureIcon },
  { id: 'trees-shrubs-and-bushes', label: 'Trees, Shrubs & Bushes', icon: NatureIcon },
  { id: 'lawn-cleanup', label: 'Lawn Cleanup', icon: CleaningServicesIcon },
  {
    id: 'landscaping-outdoor-improvements',
    label: 'Landscaping & Outdoor Improvements',
    icon: ParkIcon,
  },
  { id: 'irrigation-watering', label: 'Irrigation & Watering', icon: WaterIcon },
  { id: 'hardscaping', label: 'Hardscaping & Outdoor Living', icon: DeckIcon },
  { id: 'not-sure', label: 'Not sure — I need help scoping the job', icon: HelpOutlineIcon },
];

// ─── Per-category tasks (step 2) ────────────────────────────────────────────
const LAWN_MAINTENANCE_TASKS = [
  {
    id: 'mow-edge-trim',
    label: 'Mow, edge & trim (lawn & walkways)',
    icon: ContentCutIcon,
  },
  { id: 'aerate-lawn', label: 'Aerate My Lawn', icon: SpaIcon },
  { id: 'overseed-reseed', label: 'Overseed or Reseed Bare Spots', icon: YardIcon },
  { id: 'new-sod-turf', label: 'Install New Sod or Turf', icon: GrassIcon },
];

const LAWN_HEALTH_TASKS = [
  { id: 'fertilize-lawn', label: 'Fertilize My Lawn', icon: AgricultureIcon },
  { id: 'weed-control', label: 'Weed Control or Treatment', icon: LocalFloristIcon },
  { id: 'pest-grub', label: 'Pest or Grub Control', icon: BugReportIcon },
];

const TREES_SHRUBS_TASKS = [
  { id: 'trim-prune-shrubs', label: 'Trim or Prune Shrubs / Bushes', icon: ContentCutOutlinedIcon },
  { id: 'tree-trimming', label: 'Tree Trimming or Branch Removal', icon: ForestIcon },
  { id: 'remove-tree-stump', label: 'Remove a Tree or Stump', icon: ParkOutlinedIcon },
  { id: 'plant-trees-shrubs', label: 'Plant Trees or Shrubs', icon: ParkRoundedIcon },
];

const CLEANUP_SEASONAL_TASKS = [
  { id: 'leaf-removal', label: 'Leaf Removal or Cleanup', icon: LocalOfferIcon },
  { id: 'debris-brush', label: 'Remove Debris, Brush, or Branches', icon: DeleteSweepIcon },
];

const LANDSCAPING_OUTDOOR_TASKS = [
  { id: 'mulch-beds', label: 'Install or Refresh Mulch in Beds', icon: LandscapeIcon },
  { id: 'flower-beds', label: 'Plant or Redesign Flower Beds', icon: LocalFloristIcon },
  { id: 'garden-edging', label: 'Install Garden Borders or Edging', icon: BorderOuterIcon },
  { id: 'decorative-rock', label: 'Install Decorative Rock or Stone', icon: TerrainIcon },
  { id: 'grade-level', label: 'Grade or Level Yard / Fix Low Spots', icon: FoundationIcon },
  { id: 'drainage', label: 'Install or Fix Drainage (French Drain, Downspout)', icon: PlumbingIcon },
];

const IRRIGATION_TASKS = [
  { id: 'install-sprinkler', label: 'Install a Sprinkler or Irrigation System', icon: WaterIcon },
  { id: 'repair-sprinkler', label: 'Repair or Adjust Sprinkler Heads', icon: BuildIcon },
  { id: 'winterization', label: 'Sprinkler System Winterization (Blowout)', icon: AcUnitIcon },
  { id: 'spring-irrigation', label: 'Spring Irrigation Startup', icon: WbSunnyIcon },
];

const HARDSCAPING_TASKS = [
  { id: 'patio-walkway', label: 'Install a Patio or Walkway', icon: OutdoorGrillIcon },
  { id: 'retaining-wall', label: 'Build or Repair a Retaining Wall', icon: FoundationIcon },
  { id: 'landscape-lighting', label: 'Install Outdoor / Landscape Lighting', icon: LightModeIcon },
  { id: 'fire-pit', label: 'Build a Fire Pit Area', icon: FireplaceIcon },
  { id: 'pergola-shade', label: 'Install a Pergola or Shade Structure', icon: HolidayVillageIcon },
];

const NOT_SURE_TASKS = [
  { id: 'scope-with-pro', label: 'On-site visit to scope the work', icon: HomeIcon },
  { id: 'multiple-services', label: 'Multiple services — need a plan', icon: LayersIcon },
  { id: 'general-advice', label: 'General advice / not sure yet', icon: HelpOutlineIcon },
];

// ─── Shared follow-up options ───────────────────────────────────────────────
const PROPERTY_SIZE_OPTIONS = [
  { id: 'size-small', label: 'Small yard (under ~5,000 sq ft)', icon: MeetingRoomIcon },
  { id: 'size-medium', label: 'Medium lot (typical suburban)', icon: HomeIcon },
  { id: 'size-large', label: 'Large lot / estate', icon: WeekendIcon },
  { id: 'size-commercial', label: 'Commercial / multi-unit', icon: ApartmentIcon },
  { id: 'size-unsure', label: 'Not sure', icon: HelpOutlineIcon },
];

const SERVICE_FREQUENCY_OPTIONS = [
  { id: 'one-time', label: 'One-time job', icon: TodayIcon },
  { id: 'weekly', label: 'Weekly', icon: CalendarMonthIcon },
  { id: 'biweekly', label: 'Every 2 weeks', icon: DateRangeIcon },
  { id: 'monthly', label: 'Monthly', icon: CalendarMonthIcon },
  { id: 'seasonal-plan', label: 'Seasonal package', icon: ScheduleIcon },
];

const TREATMENT_AREA_OPTIONS = [
  { id: 'whole-lawn', label: 'Whole lawn', icon: GrassIcon },
  { id: 'front-only', label: 'Front yard only', icon: HomeIcon },
  { id: 'back-only', label: 'Back yard only', icon: WeekendIcon },
  { id: 'problem-spots', label: 'Specific problem areas', icon: FormatListNumberedIcon },
];

const TREE_JOB_SCALE_OPTIONS = [
  { id: 'tree-small', label: 'Small (under ~15 ft)', icon: NatureIcon },
  { id: 'tree-medium', label: 'Medium (15–30 ft)', icon: ForestIcon },
  { id: 'tree-large', label: 'Large / mature (30+ ft)', icon: ParkIcon },
  { id: 'tree-multiple', label: 'Multiple trees / shrubs', icon: LayersIcon },
  { id: 'tree-unsure', label: 'Not sure', icon: HelpOutlineIcon },
];

const CLEANUP_VOLUME_OPTIONS = [
  { id: 'light-bags', label: 'Light (few bags / small pile)', icon: LocalOfferIcon },
  { id: 'moderate', label: 'Moderate (half truck or less)', icon: DeleteSweepIcon },
  { id: 'heavy', label: 'Heavy (full truck or multiple loads)', icon: LayersIcon },
  { id: 'cleanup-unsure', label: 'Not sure', icon: HelpOutlineIcon },
];

const OUTDOOR_PROJECT_SCOPE_OPTIONS = [
  { id: 'scope-single-bed', label: 'Single bed or small area', icon: LocalFloristIcon },
  { id: 'scope-front', label: 'Front yard', icon: HomeIcon },
  { id: 'scope-back', label: 'Back yard', icon: WeekendIcon },
  { id: 'scope-whole', label: 'Whole property', icon: YardIcon },
  { id: 'scope-unsure', label: 'Not sure yet', icon: HelpOutlineIcon },
];

const IRRIGATION_CONTEXT_OPTIONS = [
  { id: 'irr-new-install', label: 'New system / first-time install', icon: BuildIcon },
  { id: 'irr-existing-repair', label: 'Existing system — repair or upgrade', icon: PlumbingIcon },
  { id: 'irr-seasonal-service', label: 'Seasonal service only (startup / blowout)', icon: AcUnitIcon },
  { id: 'irr-unsure', label: 'Not sure', icon: HelpOutlineIcon },
];

const URGENCY_OPTIONS = [
  { id: 'asap', label: 'As soon as possible', icon: ScheduleIcon },
  { id: 'this-week', label: 'Within the next week', icon: TodayIcon },
  { id: 'few-weeks', label: 'Within the next few weeks', icon: DateRangeIcon },
  { id: 'just-quotes', label: 'Just getting quotes', icon: RequestQuoteIcon },
];

const FLOW_BRANCHES = {
  'lawn-maintenance': [
    {
      key: 'landscapingTask',
      title: 'What lawn maintenance do you need?',
      subtitle: 'Mowing with edging and trimming is usually one service — pick what fits.',
      icon: GrassIcon,
      options: LAWN_MAINTENANCE_TASKS,
    },
    {
      key: 'propertySize',
      title: 'Roughly how big is the area?',
      subtitle: 'Helps pros bring the right crew and equipment.',
      icon: YardIcon,
      options: PROPERTY_SIZE_OPTIONS,
    },
    {
      key: 'lawnFrequency',
      title: 'How often do you need service?',
      subtitle: 'Great for recurring mowing & edging and ongoing maintenance plans.',
      icon: CalendarMonthIcon,
      options: SERVICE_FREQUENCY_OPTIONS,
      condition: (fd) => fd.landscapingTask === 'mow-edge-trim',
    },
  ],
  'lawn-health-and-treatment': [
    {
      key: 'landscapingTask',
      title: 'What kind of lawn treatment?',
      subtitle: 'Fertilizer, weeds, or pests — we’ll match a lawn care pro.',
      icon: AgricultureIcon,
      options: LAWN_HEALTH_TASKS,
    },
    {
      key: 'propertySize',
      title: 'Roughly how big is the lawn?',
      subtitle: 'Product amounts and pricing scale with lot size.',
      icon: YardIcon,
      options: PROPERTY_SIZE_OPTIONS,
    },
    {
      key: 'treatmentArea',
      title: 'Where should treatment focus?',
      subtitle: 'Whole lawn vs. trouble spots changes the approach.',
      icon: GrassIcon,
      options: TREATMENT_AREA_OPTIONS,
    },
  ],
  'trees-shrubs-and-bushes': [
    {
      key: 'landscapingTask',
      title: 'What do you need for trees or shrubs?',
      subtitle: 'Trimming, removal, and planting.',
      icon: NatureIcon,
      options: TREES_SHRUBS_TASKS,
    },
    {
      key: 'propertySize',
      title: 'Roughly how big is the property?',
      subtitle: 'Access and equipment depend on layout and size.',
      icon: YardIcon,
      options: PROPERTY_SIZE_OPTIONS,
    },
    {
      key: 'treeJobScale',
      title: 'How big is the tree or scope?',
      subtitle: 'Skip if not applicable — best guess is fine.',
      icon: ForestIcon,
      options: TREE_JOB_SCALE_OPTIONS,
      condition: (fd) =>
        fd.landscapingTask === 'tree-trimming' ||
        fd.landscapingTask === 'remove-tree-stump' ||
        fd.landscapingTask === 'trim-prune-shrubs',
    },
  ],
  'lawn-cleanup': [
    {
      key: 'landscapingTask',
      title: 'What kind of cleanup?',
      subtitle: 'Leaf cleanup and debris removal — we’ll match the right crew.',
      icon: CleaningServicesIcon,
      options: CLEANUP_SEASONAL_TASKS,
    },
    {
      key: 'propertySize',
      title: 'Roughly how big is the property?',
      subtitle: 'Crew size and haul-away depend on lot size.',
      icon: YardIcon,
      options: PROPERTY_SIZE_OPTIONS,
    },
    {
      key: 'cleanupVolume',
      title: 'How much debris or leaves?',
      subtitle: 'Helps us quote trucks, bags, and labor.',
      icon: DeleteSweepIcon,
      options: CLEANUP_VOLUME_OPTIONS,
      condition: (fd) =>
        fd.landscapingTask === 'leaf-removal' || fd.landscapingTask === 'debris-brush',
    },
  ],
  'landscaping-outdoor-improvements': [
    {
      key: 'landscapingTask',
      title: 'What outdoor project do you have in mind?',
      subtitle: 'Mulch, beds, edging, rock, grading, or drainage.',
      icon: ParkIcon,
      options: LANDSCAPING_OUTDOOR_TASKS,
    },
    {
      key: 'propertySize',
      title: 'Roughly how big is the property?',
      subtitle: 'Design and material quantities scale with area.',
      icon: YardIcon,
      options: PROPERTY_SIZE_OPTIONS,
    },
    {
      key: 'outdoorProjectScope',
      title: 'How much of the yard is involved?',
      subtitle: 'One bed vs. whole-property makeover.',
      icon: LandscapeIcon,
      options: OUTDOOR_PROJECT_SCOPE_OPTIONS,
    },
  ],
  'irrigation-watering': [
    {
      key: 'landscapingTask',
      title: 'What irrigation help do you need?',
      subtitle: 'Install, repair, winterization, or spring startup.',
      icon: WaterIcon,
      options: IRRIGATION_TASKS,
    },
    {
      key: 'propertySize',
      title: 'Roughly how big is the irrigated area?',
      subtitle: 'Zone count and pipe runs depend on coverage.',
      icon: YardIcon,
      options: PROPERTY_SIZE_OPTIONS,
    },
    {
      key: 'irrigationContext',
      title: 'Tell us more about your system',
      subtitle: 'New install vs. fixing an existing system shapes the visit.',
      icon: BuildIcon,
      options: IRRIGATION_CONTEXT_OPTIONS,
    },
  ],
  hardscaping: [
    {
      key: 'landscapingTask',
      title: 'What hardscaping or outdoor living project?',
      subtitle: 'Patios, walls, lighting, fire features, and shade structures.',
      icon: DeckIcon,
      options: HARDSCAPING_TASKS,
    },
    {
      key: 'propertySize',
      title: 'Roughly how big is the work area?',
      subtitle: 'Square footage drives materials and crew size.',
      icon: YardIcon,
      options: PROPERTY_SIZE_OPTIONS,
    },
    {
      key: 'outdoorProjectScope',
      title: 'How extensive is the project?',
      subtitle: 'Small accent vs. full outdoor living space.',
      icon: LandscapeIcon,
      options: OUTDOOR_PROJECT_SCOPE_OPTIONS,
    },
  ],
  'not-sure': [
    {
      key: 'landscapingTask',
      title: 'What sounds closest?',
      subtitle: 'We’ll still match you — a pro can refine the scope on site.',
      icon: HelpOutlineIcon,
      options: NOT_SURE_TASKS,
    },
    {
      key: 'propertySize',
      title: 'Roughly how big is the property?',
      subtitle: 'Helps narrow down the right landscapers.',
      icon: YardIcon,
      options: PROPERTY_SIZE_OPTIONS,
    },
  ],
};

const SHARED_TAIL_KEYS = ['urgency', 'photos', 'address', 'review'];

const STEP_LABELS = {
  problem: {
    title: 'What kind of landscaping help?',
    subtitle: 'Choose a category — you can narrow it down on the next steps.',
    icon: GrassIcon,
  },
  urgency: {
    title: 'When do you need help?',
    subtitle: 'Busy seasons fill fast — your timeline helps us prioritize.',
    icon: ScheduleIcon,
  },
  photos: {
    title: 'Photos & details',
    subtitle: 'Optional — photos help pros estimate before they visit.',
    icon: AddPhotoAlternateIcon,
  },
  address: {
    title: 'What city is the job in?',
    subtitle:
      'Enter the city and state so we can match you with pros in your area. No street address needed.',
    icon: HomeIcon,
  },
  review: {
    title: 'Review your request',
    subtitle: 'Make sure everything looks good before we find your pro.',
    icon: CheckCircleOutlineIcon,
  },
};

const ILLUSTRATION_CAPTIONS = {
  problem: 'From weekly mowing to full outdoor projects — tell us what you need.',
  landscapingTask: 'Specific tasks help us match crews with the right skills and equipment.',
  propertySize: 'Lot size drives crew size, materials, and how long the job takes.',
  lawnFrequency: 'Recurring routes need different scheduling than one-time visits.',
  treatmentArea: 'Spot-treating vs. whole lawn changes product rates and timing.',
  treeJobScale: 'Tree work pricing and safety gear depend on height and spread.',
  cleanupVolume: 'Haul-away and bag counts depend on how heavy the cleanup is.',
  outdoorProjectScope: 'Scope tells designers and installers how to plan materials and labor.',
  irrigationContext: 'New installs vs. repairs need different parts and diagnostics.',
  urgency: 'Spring and fall are peak — timing affects availability.',
  photos: 'Wide shots of the yard and problem areas help pros prepare.',
  address: "We'll use the city and state to match you with pros who work in your area.",
  review: "You're almost done — review and we'll connect you with local pros.",
};

export const landscapingSubserviceFlowConfig = {
  problemStep: {
    options: LANDSCAPING_PROBLEMS,
    ...STEP_LABELS.problem,
  },
  flowBranches: FLOW_BRANCHES,
  sharedTailKeys: SHARED_TAIL_KEYS,
  urgencyStep: {
    options: URGENCY_OPTIONS,
    ...STEP_LABELS.urgency,
  },
  photosStep: STEP_LABELS.photos,
  addressStep: STEP_LABELS.address,
  reviewStep: STEP_LABELS.review,
  illustrationCaptions: ILLUSTRATION_CAPTIONS,
  submitButtonLabel: 'Find my landscaping pro',
};
